import React, { useState, useEffect } from 'react';
import { Box, Grid, useTheme } from '@mui/material';
import { usePreferences } from '../../contexts/PreferencesContext';
import AiService from '../../services/ai.service';
import AsistentHero from './AsistentHero';
import AsistentUploadZone from './AsistentUploadZone';
import AsistentDocumentList from './AsistentDocumentList';
import AsistentTestChat from './AsistentTestChat';

const AsistentPage = () => {
    const { t } = usePreferences();
    const theme = useTheme();

    const [uploading, setUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [chatLoading, setChatLoading] = useState(false);
    const [chatError, setChatError] = useState(null);

    const loadDocuments = () => {
        setDocumentsLoading(true);
        AiService.listRagDocuments()
            .then((res) => setDocuments(Array.isArray(res.data?.documents) ? res.data.documents : []))
            .catch(() => setDocuments([]))
            .finally(() => setDocumentsLoading(false));
    };

    useEffect(() => loadDocuments(), []);

    const handleUpload = (file, onSuccess) => {
        if (!file) {
            setUploadStatus({ type: 'error', text: t('pages.asistent.uploadSelectError') });
            return;
        }
        setUploading(true);
        setUploadStatus(null);
        AiService.uploadRagDocument(file)
            .then(() => {
                setUploadStatus({ type: 'success', text: t('pages.asistent.uploadSuccess') });
                onSuccess?.();
                loadDocuments();
            })
            .catch((err) => {
                const msg = err.response?.data?.message || err.message || t('pages.asistent.uploadFailed');
                setUploadStatus({ type: 'error', text: msg });
            })
            .finally(() => setUploading(false));
    };

    const handleDelete = (documentId) => {
        setDeletingId(documentId);
        AiService.deleteRagDocument(documentId)
            .then(() => loadDocuments())
            .finally(() => setDeletingId(null));
    };

    const handleSend = () => {
        const text = (input || '').trim();
        if (!text || chatLoading) return;
        const userMessage = { role: 'user', content: text };
        setMessages((prev) => [...prev, userMessage]);
        setInput('');
        setChatError(null);
        setChatLoading(true);
        const nextMessages = [...messages, userMessage];
        AiService.ragChat(nextMessages)
            .then((res) => {
                const answer = res.data?.answer ?? '';
                setMessages((prev) => [...prev, { role: 'assistant', content: answer }]);
            })
            .catch((err) => {
                const msg = err.response?.data?.message || err.message || t('pages.asistent.chatFailed');
                setChatError(msg);
            })
            .finally(() => setChatLoading(false));
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
            <AsistentHero t={t} theme={theme} />

            <Grid container spacing={2.5} alignItems="stretch">
                <Grid size={{ xs: 12, lg: 6 }}>
                    <AsistentUploadZone
                        t={t}
                        theme={theme}
                        uploading={uploading}
                        uploadStatus={uploadStatus}
                        onUpload={handleUpload}
                    />
                    <AsistentDocumentList
                        t={t}
                        theme={theme}
                        documents={documents}
                        loading={documentsLoading}
                        deletingId={deletingId}
                        onDelete={handleDelete}
                    />
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
                    <Box sx={{ flex: 1, width: '100%' }}>
                        <AsistentTestChat
                            t={t}
                            theme={theme}
                            messages={messages}
                            input={input}
                            onInputChange={(e) => setInput(e.target.value)}
                            onSend={handleSend}
                            loading={chatLoading}
                            error={chatError}
                        />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AsistentPage;
